import { Controller } from "@hotwired/stimulus";

/**
 * Scrolls to the top of the page.
 * A use case is on turbo:submit-end, where the form may render errors after
 * submission and we want the user to see the errors.
 *
 * The scrollable element can be specified with the scrollableElementValue.
 * If not specified, the window is scrolled to the top.
 *
 * The scrollable element must be a string selector.
 * It will be passed to document.querySelector.
 */
export default class extends Controller {
  static values = {
    scrollableElement: {
      type: String,
      default: "window",
    },
    behavior: {
      type: String,
      default: "smooth",
    },
    scrollOnConnect: {
      type: Boolean,
      default: false,
    },
  };

  connect() {
    if (this.scrollOnConnectValue) {
      this.scrollToTop();
    }
  }

  scrollToTop() {
    // Wait some delay, otherwise scroll could happen before the page is rendered
    // and transitioned, so the scroll is interrupted.
    const framesToWait = this.isSavingData() || this.hasSlowInternet() ? 18 : 6; // ~300ms vs ~100ms at 60fps

    this.waitFrames(framesToWait, () => {
      if (this.scrollableElementValue === "window") {
        window.scrollTo({
          top: 0,
          behavior: this.behaviorValue,
        });
      } else {
        document.querySelector(this.scrollableElementValue).scrollTo({
          top: 0,
          behavior: this.behaviorValue,
        });
      }
    });
  }

  waitFrames(frameCount, callback) {
    if (frameCount <= 0) {
      callback();
      return;
    }

    requestAnimationFrame(() => {
      this.waitFrames(frameCount - 1, callback);
    });
  }

  isSavingData() {
    return navigator.connection?.saveData;
  }

  hasSlowInternet() {
    return (
      navigator.connection?.effectiveType === "slow-2g" ||
      navigator.connection?.effectiveType === "2g"
    );
  }
}
